import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  LinearProgress,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useTranslation } from "react-i18next";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../firebaseConfig";
import { createExercise, updateExercise, getExercise } from "../services/exerciseAiService";

const MODES = [
  { value: "hints", label: "تلميحات فقط" },
  { value: "guided", label: "حل موجّه خطوة بخطوة" },
  { value: "direct", label: "إجابة أكثر مباشرة" },
];

const ACCEPTED_TYPES = "application/pdf,image/*";
const MAX_SUBJECT_IMAGES = 12;
const MAX_CORRECTION_IMAGES = 6;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const isPdf = (file) => file?.type === "application/pdf";
const isImage = (file) => Boolean(file?.type?.startsWith("image/"));
const looksLikeImageName = (name) => /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(name || "");

/**
 * Validates a set of picked files for one side (subject or
 * correction): either exactly one PDF, or 1..maxImages images —
 * never a mix, never an oversized file. Returns an error message
 * (already translated) or null when the selection is valid.
 */
function validateFileGroup(files, maxImages, t) {
  if (!files.length) return null;
  const pdfCount = files.filter(isPdf).length;
  const imageCount = files.filter(isImage).length;
  const otherCount = files.length - pdfCount - imageCount;

  if (otherCount > 0) {
    return t("Please attach the exercise as a PDF or an image.");
  }
  if (pdfCount > 0 && imageCount > 0) {
    return t("Choose either a single PDF, or one or more images — not both together.");
  }
  if (pdfCount > 1) {
    return t("Only one PDF file is allowed.");
  }
  if (imageCount > maxImages) {
    return t("You can attach up to {{max}} images.", { max: maxImages });
  }
  const tooBig = files.find((f) => f.size > MAX_IMAGE_BYTES);
  if (tooBig) {
    return t("Each file must be under 20 MB.");
  }
  return null;
}

function FilePreviewList({ files, onRemove }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {files.map((file, index) => {
        const isImg = isImage(file);
        const objectUrl = isImg ? URL.createObjectURL(file) : null;
        return (
          <Box
            key={`${file.name}-${index}`}
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: 1.5,
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f8fafc",
            }}
          >
            {isImg ? (
              <Box
                component="img"
                src={objectUrl}
                onLoad={() => URL.revokeObjectURL(objectUrl)}
                alt={file.name}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, px: 0.5 }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: "#64748b" }} />
                <Typography sx={{ fontSize: 9, color: "#64748b", textAlign: "center", wordBreak: "break-all" }}>
                  {file.name}
                </Typography>
              </Box>
            )}
            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                bgcolor: "rgba(15,23,42,0.6)",
                color: "#fff",
                width: 20,
                height: 20,
                "&:hover": { bgcolor: "rgba(15,23,42,0.8)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}

function ExistingImagesPreview({ images }) {
  if (!images.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {images.map((img, index) => (
        <Box
          key={`${img.url}-${index}`}
          component="img"
          src={img.url}
          alt={img.name || `image-${index}`}
          sx={{
            width: 72,
            height: 72,
            objectFit: "cover",
            borderRadius: 1.5,
            border: "1px solid #e5e7eb",
          }}
        />
      ))}
    </Box>
  );
}

export default function TeacherAddExercise({ groupId, exerciseId, onClose, onCreated }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isEditMode = Boolean(exerciseId);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState("hints");
  const [aiEnabled, setAiEnabled] = useState(true);
  const [correctionText, setCorrectionText] = useState("");
  const [teacherInstructions, setTeacherInstructions] = useState("");
  const [correctionFiles, setCorrectionFiles] = useState([]);
  const [hasExistingCorrectionFile, setHasExistingCorrectionFile] = useState(false);
  const [subjectFiles, setSubjectFiles] = useState([]);
  const [existingSubjectPdfName, setExistingSubjectPdfName] = useState("");
  const [existingSubjectImages, setExistingSubjectImages] = useState([]);
  const [subjectText, setSubjectText] = useState("");
  const [progress, setProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEditMode) return;

    getExercise({ groupId, exerciseId })
      .then(({ exercise }) => {
        setTitle(exercise.title || "");
        setCategory(exercise.category || "");
        setMode(exercise.mode || "hints");
        setAiEnabled(Boolean(exercise.aiEnabled));
        setCorrectionText(exercise.correctionText || "");
        setTeacherInstructions(exercise.teacherInstructions || "");
        setHasExistingCorrectionFile(Boolean(exercise.hasCorrectionFile));
        setExistingSubjectPdfName(exercise.subjectPdfName || "");
        setSubjectText(exercise.subjectText || "");
        if (exercise.subjectImages?.length) {
          setExistingSubjectImages(exercise.subjectImages);
        } else if (exercise.subjectPdfUrl && looksLikeImageName(exercise.subjectPdfName)) {
          setExistingSubjectImages([{ url: exercise.subjectPdfUrl, name: exercise.subjectPdfName }]);
        }
      })
      .catch((err) => setError(err.message || t("Unable to load the exercise.")))
      .finally(() => setIsLoading(false));
  }, [isEditMode, groupId, exerciseId, t]);

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Uploads every file to Storage in parallel and tracks a combined
  // progress percentage across all of them — used for the subject
  // side (PDF or images), never for the correction side (which never
  // touches Storage, see uploadFilesAsBase64 below).
  const uploadFilesToStorage = (files) => {
    const progressByIndex = new Array(files.length).fill(0);
    const reportProgress = () => {
      const total = progressByIndex.reduce((a, b) => a + b, 0) / files.length;
      setProgress(Math.round(total));
    };

    return Promise.all(
      files.map(
        (file, index) =>
          new Promise((resolve, reject) => {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
            // Same flat path convention as TeacherAddLesson.js (groups/{groupId}/{fileName}) —
            // the index avoids collisions when several files share the same Date.now() tick.
            const storageRef = ref(storage, `groups/${groupId}/${Date.now()}-${index}-${safeName}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
              "state_changed",
              (snapshot) => {
                progressByIndex[index] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                reportProgress();
              },
              reject,
              async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                resolve({ url, name: file.name, mimeType: file.type });
              }
            );
          })
      )
    );
  };

  const removeSubjectFile = (index) => {
    setSubjectFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const removeCorrectionFile = (index) => {
    setCorrectionFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError("");

    if (!title.trim()) {
      setError(t("Please enter a title."));
      return;
    }
    if (!isEditMode && !subjectFiles.length) {
      setError(t("Please attach the exercise as a PDF or an image."));
      return;
    }
    const subjectError = !isEditMode ? validateFileGroup(subjectFiles, MAX_SUBJECT_IMAGES, t) : null;
    if (subjectError) {
      setError(subjectError);
      return;
    }
    const correctionError = validateFileGroup(correctionFiles, MAX_CORRECTION_IMAGES, t);
    if (correctionError) {
      setError(correctionError);
      return;
    }
    if (!isEditMode && !correctionText.trim() && !correctionFiles.length) {
      setError(t("Add the correction as text and/or a file — it is never shown to students."));
      return;
    }

    try {
      setIsSaving(true);
      setProgress(0);

      const correctionIsPdf = correctionFiles.length === 1 && isPdf(correctionFiles[0]);
      const correctionPdfBase64 = correctionIsPdf ? await readFileAsBase64(correctionFiles[0]) : "";
      const correctionImages = !correctionIsPdf && correctionFiles.length
        ? await Promise.all(
            correctionFiles.map(async (file) => ({
              base64: await readFileAsBase64(file),
              mimeType: file.type,
            }))
          )
        : [];

      if (isEditMode) {
        await updateExercise({
          groupId,
          exerciseId,
          title: title.trim(),
          category: category.trim(),
          mode,
          aiEnabled,
          correctionText: correctionText.trim(),
          correctionPdfBase64,
          correctionImages,
          teacherInstructions: teacherInstructions.trim(),
        });
      } else {
        const subjectIsPdf = subjectFiles.length === 1 && isPdf(subjectFiles[0]);
        const uploaded = await uploadFilesToStorage(subjectFiles);
        const subjectPdfBase64 = subjectIsPdf ? await readFileAsBase64(subjectFiles[0]) : "";

        await createExercise({
          groupId,
          title: title.trim(),
          category: category.trim(),
          mode,
          aiEnabled,
          subjectPdfUrl: uploaded[0]?.url || "",
          subjectPdfName: subjectFiles[0].name,
          subjectPdfBase64,
          subjectFileType: subjectIsPdf ? "pdf" : "image",
          subjectImages: subjectIsPdf ? [] : uploaded,
          correctionText: correctionText.trim(),
          correctionPdfBase64,
          correctionImages,
          teacherInstructions: teacherInstructions.trim(),
        });
      }

      onCreated?.();
      onClose();
    } catch (err) {
      setError(err.message || t("Unable to save the exercise."));
    } finally {
      setIsSaving(false);
    }
  };

  const correctionButtonLabel = correctionFiles.length
    ? t("{{count}} file(s) selected", { count: correctionFiles.length })
    : hasExistingCorrectionFile
    ? t("استبدال ملف التصحيح الحالي (PDF أو صورة/صور)")
    : t("إرفاق التصحيح كملف PDF أو صورة/صور (اختياري)");

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>
        {isEditMode ? t("تعديل تمرين Darssy AI") : t("إضافة تمرين مع Darssy AI")}
      </DialogTitle>

      {isLoading ? (
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </DialogContent>
      ) : (
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, direction: "rtl" }}>
          <TextField
            label={t("عنوان التمرين")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />

          <TextField
            label={t("الفئة (اختياري)")}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="exercise-mode-label">{t("نمط المساعد")}</InputLabel>
            <Select
              labelId="exercise-mode-label"
              value={mode}
              label={t("نمط المساعد")}
              onChange={(e) => setMode(e.target.value)}
            >
              {MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {t(m.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />}
            label={t("تفعيل Darssy AI لهذا التمرين")}
          />

          {isEditMode ? (
            <>
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                {t("ملف التمرين الحالي: {{name}} (لا يمكن تغييره من هنا)", {
                  name: existingSubjectPdfName || t("غير معروف"),
                })}
              </Typography>
              <ExistingImagesPreview images={existingSubjectImages} />
              {subjectText && (
                <TextField
                  label={t("ما فهمه Darssy AI من ملف التمرين (للمراجعة فقط)")}
                  value={subjectText}
                  multiline
                  minRows={4}
                  maxRows={10}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-input": { color: "#475569" } }}
                />
              )}
            </>
          ) : (
            <>
              <Button variant="outlined" component="label">
                {subjectFiles.length
                  ? t("{{count}} file(s) selected", { count: subjectFiles.length })
                  : t("إرفاق نص التمرين (PDF أو صورة/صور)")}
                <input
                  type="file"
                  accept={ACCEPTED_TYPES}
                  multiple
                  hidden
                  onChange={(e) => setSubjectFiles(Array.from(e.target.files || []))}
                />
              </Button>
              <FilePreviewList files={subjectFiles} onRemove={removeSubjectFile} />
            </>
          )}

          <Button variant="outlined" component="label">
            {correctionButtonLabel}
            <input
              type="file"
              accept={ACCEPTED_TYPES}
              multiple
              hidden
              onChange={(e) => setCorrectionFiles(Array.from(e.target.files || []))}
            />
          </Button>
          <FilePreviewList files={correctionFiles} onRemove={removeCorrectionFile} />

          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            {t("يقرأ Darssy AI الملف مباشرة (PDF أو صورة، نص ورسوم ومخططات)، لا يظهر أبداً للطالب.")}
          </Typography>

          <TextField
            label={t("نص التصحيح (مستخرج من الملف + أي إضافات، لن يظهر للطالب)")}
            value={correctionText}
            onChange={(e) => setCorrectionText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
          />

          <TextField
            label={t("تعليمات أو سياق إضافي لِـ Darssy AI (اختياري، لن يظهر للطالب)")}
            placeholder={t("مثال: هذا التمرين موجّه لطلاب ضعاف، خذ وقتك في الشرح. ركّز على المصطلحات المستعملة في الدرس...")}
            value={teacherInstructions}
            onChange={(e) => setTeacherInstructions(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          {progress > 0 && progress < 100 && <LinearProgress variant="determinate" value={progress} />}

          {error && (
            <Typography sx={{ color: "red", textAlign: "center" }}>{error}</Typography>
          )}
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          {t("Cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || isLoading}>
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
